/**
 * Item #14 — the Birdseye pan/zoom camera, extracted so the Guided Start bay
 * picker uses the SAME gestures (mouse drag, wheel zoom, one-finger pan,
 * two-finger pinch, fit to screen) instead of a second implementation.
 *
 * Behaviour is the Birdseye page's, moved verbatim: content is laid out in
 * layout coordinates relative to `bounds.minX/minY` inside a transformed div
 * (`translate(offset) scale(zoom)`, origin 0 0). Nothing here knows about
 * tickets, realtime or drag-and-drop; the page passes `panBlocked` while
 * dnd-kit owns the pointer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

// OCT28 RECOVERY #4: 0.05, not 0.2 — a fit must be able to FIT. A real floor plan (Floyd's: ~5,600 layout units
// wide) on a phone needs ~0.07; clamped at 0.2 the "fitted" map was 3× too wide and its bays fell off screen.
export const MAP_MIN_ZOOM = 0.05;
export const MAP_MAX_ZOOM = 2.5;

/** Movement beyond this (px) is a pan, not a tap. */
export const MAP_DRAG_THRESHOLD = 5;

export interface MapRect { x: number; y: number; w: number; h: number }

export interface MapBounds { minX: number; minY: number; width: number; height: number; pad: number }

/** Birdseye's rule: a press on a control never starts a pan. */
export function isNonPanTarget(target: HTMLElement): boolean {
  return (
    target.tagName === 'BUTTON' ||
    !!target.closest('button') ||
    !!target.closest('[role="button"]') ||
    !!target.closest('input') ||
    !!target.closest('select') ||
    !!target.closest('textarea') ||
    !!target.closest('[data-draggable-ticket]')
  );
}

export interface FitOptions {
  /** Never fit smaller than this (tap targets stay usable on a phone). */
  minZoom?: number;
  /** When the fit is clamped up, centre this rect instead of the whole layout. */
  focus?: MapRect | null;
  /**
   * Fit to these rects instead of every item (the Guided Start picker frames the
   * selectable bays; a far-off or unplaced parking spot must not shrink them).
   * Content is still positioned against the bounds of ALL items.
   */
  rects?: readonly MapRect[] | null;
  /** Screen-pixel margin around the fitted rects. Default: the historical 60 layout units. */
  padPx?: number;
}

export interface UseMapCameraOptions {
  /** Every rect drawn on the map, in layout coordinates. */
  items: readonly MapRect[];
  /** True while something else owns the pointer (Birdseye: a ticket drag). */
  panBlocked?: boolean;
  /** Which presses never start a pan. Default: Birdseye's controls rule. */
  ignorePanStart?: (target: HTMLElement) => boolean;
  /** Re-attach the wheel listener when the container (re)mounts. */
  attachKey?: unknown;
}

export function useMapCamera({
  items,
  panBlocked = false,
  ignorePanStart = isNonPanTarget,
  attachKey,
}: UseMapCameraOptions) {
  // Drag and pan state - use refs to avoid stale closures during drag
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const dragStartOffsetRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const [bounds, setBounds] = useState<MapBounds>({ minX: 0, minY: 0, width: 1600, height: 1200, pad: 200 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });

  // Zoom state
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1); // Tracks current zoom for rAF loop (avoids stale closure)
  const targetZoomRef = useRef(1);
  const isZoomAnimatingRef = useRef(false);
  const targetOffsetRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef<number | null>(null);
  const [isPinching, setIsPinching] = useState(false);
  // Set by a real user gesture on the map (pan, pinch, wheel) — never by a press on a
  // control — so an automatic refit (resize) can leave a deliberate view alone.
  const userMovedRef = useRef(false);

  // Calculate the proper bounds when the items change
  useEffect(() => {
    if (items.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    items.forEach(item => {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.w);
      maxY = Math.max(maxY, item.y + item.h);
    });

    const padding = 200;
    setBounds({
      minX: minX - padding,
      minY: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
      pad: padding,
    });
  }, [items]);

  /** Jump (no animation) to an offset — e.g. auto-pan to a bay. */
  const panTo = useCallback((newOffset: { x: number; y: number }) => {
    offsetRef.current = newOffset;
    targetOffsetRef.current = newOffset;
    setOffset(newOffset);
  }, []);

  // Fit-to-screen function: calculates scale and offset to center content
  const fitToScreen = useCallback((opts: FitOptions = {}) => {
    if (!containerRef.current || items.length === 0) return;

    const container = containerRef.current;
    const viewportW = container.clientWidth;
    const viewportH = container.clientHeight;

    // Content is laid out relative to the bounds of ALL items (see the bounds effect).
    let allMinX = Infinity, allMinY = Infinity;
    items.forEach(item => {
      allMinX = Math.min(allMinX, item.x);
      allMinY = Math.min(allMinY, item.y);
    });

    // Bounding box of what this fit frames: the given rects, else every item.
    const framed = opts.rects && opts.rects.length > 0 ? opts.rects : items;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    framed.forEach(item => {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.w);
      maxY = Math.max(maxY, item.y + item.h);
    });

    // Calculate scale to fit viewport — a screen-pixel margin when asked for, else the
    // historical 60 layout units on each side.
    let scaleX: number, scaleY: number;
    if (opts.padPx != null) {
      const pad = Math.max(0, opts.padPx);
      scaleX = Math.max(1, viewportW - pad * 2) / Math.max(1, maxX - minX);
      scaleY = Math.max(1, viewportH - pad * 2) / Math.max(1, maxY - minY);
    } else {
      const padding = 60;
      scaleX = viewportW / (maxX - minX + padding * 2);
      scaleY = viewportH / (maxY - minY + padding * 2);
    }
    const fitted = Math.min(Math.max(MAP_MIN_ZOOM, Math.min(scaleX, scaleY)), MAP_MAX_ZOOM);
    const clamped = opts.minZoom != null && fitted < opts.minZoom;
    const scale = clamped ? Math.min(opts.minZoom as number, MAP_MAX_ZOOM) : fitted;

    // Sync target ref and stop any animation
    targetZoomRef.current = scale;
    isZoomAnimatingRef.current = false;
    zoomRef.current = scale;
    setZoom(scale);

    // Calculate offset to center content
    // Content coords are relative to bounds.minX/minY, so the center of content in content-space:
    // (bounds' minX/minY are recomputed here so a fit right after the items change is not stale)
    const bMinX = allMinX - 200;
    const bMinY = allMinY - 200;
    const focus = clamped && opts.focus ? opts.focus : null;
    // The centre of the framed box itself — the fit margin is symmetric, so it never shifts the centre.
    const contentCx = focus ? focus.x + focus.w / 2 - bMinX : (minX + maxX) / 2 - bMinX;
    const contentCy = focus ? focus.y + focus.h / 2 - bMinY : (minY + maxY) / 2 - bMinY;

    panTo({
      x: viewportW / 2 - contentCx * scale,
      y: viewportH / 2 - contentCy * scale,
    });
  }, [items, panTo]);

  // Smooth zoom animation loop — uses refs only, no React state dependency per frame
  const animateZoom = useCallback(() => {
    if (!isZoomAnimatingRef.current) return;

    const currentZoom = zoomRef.current;
    const diff = targetZoomRef.current - currentZoom;
    const currentOffset = offsetRef.current;
    const tOff = targetOffsetRef.current;
    const offDiffX = tOff.x - currentOffset.x;
    const offDiffY = tOff.y - currentOffset.y;

    // Stop animating when close enough
    if (Math.abs(diff) < 0.002 && Math.abs(offDiffX) < 0.5 && Math.abs(offDiffY) < 0.5) {
      zoomRef.current = targetZoomRef.current;
      offsetRef.current = tOff;
      setZoom(targetZoomRef.current);
      setOffset(tOff);
      isZoomAnimatingRef.current = false;
      return;
    }

    // Lerp toward target with easing factor
    const easingFactor = 0.18;
    const newZoom = currentZoom + diff * easingFactor;
    const newOffset = {
      x: currentOffset.x + offDiffX * easingFactor,
      y: currentOffset.y + offDiffY * easingFactor,
    };
    zoomRef.current = newZoom;
    offsetRef.current = newOffset;

    // Batch React state update
    setZoom(newZoom);
    setOffset(newOffset);

    requestAnimationFrame(animateZoom);
  }, []); // No deps — reads only from refs

  // Mouse wheel zoom (delta-proportional, cursor-centered with smooth animation)
  const handleWheel = useCallback((e: WheelEvent) => {
    // ALWAYS prevent default scroll on the map - zoom only
    e.preventDefault();

    // Don't zoom while something else owns the pointer
    if (panBlocked) return;

    const container = containerRef.current;
    if (!container) return;
    userMovedRef.current = true;

    // Get cursor position relative to container
    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;

    // Calculate point in content space using current target for smooth accumulation
    const currentZoom = isZoomAnimatingRef.current ? targetZoomRef.current : zoomRef.current;
    const currentOffset = isZoomAnimatingRef.current ? targetOffsetRef.current : offsetRef.current;

    // Delta-proportional zoom (trackpad-friendly) - accumulate into target
    const zoomFactor = Math.exp(-e.deltaY * 0.002);
    const newZoom = Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, currentZoom * zoomFactor));
    targetZoomRef.current = newZoom;

    // Adjust offset so point under cursor stays fixed
    // cursorX = contentX * zoom + offsetX  =>  contentX = (cursorX - offsetX) / zoom
    // After zoom change: newOffsetX = cursorX - contentX * newZoom
    targetOffsetRef.current = {
      x: cursorX - (cursorX - currentOffset.x) * (newZoom / currentZoom),
      y: cursorY - (cursorY - currentOffset.y) * (newZoom / currentZoom),
    };

    // Start animation if not already running
    if (!isZoomAnimatingRef.current) {
      isZoomAnimatingRef.current = true;
      requestAnimationFrame(animateZoom);
    }
  }, [panBlocked, animateZoom]);

  // Attach wheel listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel, attachKey]);

  const onMouseDown = (e: React.MouseEvent) => {
    // Disable pan while something else owns the pointer
    if (panBlocked) return;

    // Only activate drag if not clicking on interactive elements
    if (e.button !== 0) return; // left-click only
    if (ignorePanStart(e.target as HTMLElement)) return;

    if (!containerRef.current) return;

    // If a zoom animation is in-flight, stop it so panning stays responsive.
    isZoomAnimatingRef.current = false;
    targetOffsetRef.current = offsetRef.current;
    targetZoomRef.current = zoomRef.current;

    hasDraggedRef.current = false; // Reset drag flag
    isDraggingRef.current = true;
    setIsDragging(true);
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    dragStartOffsetRef.current = { ...offsetRef.current };
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();

    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;

    // Mark as dragged if moved beyond threshold
    if (Math.abs(dx) > MAP_DRAG_THRESHOLD || Math.abs(dy) > MAP_DRAG_THRESHOLD) {
      hasDraggedRef.current = true;
      userMovedRef.current = true;
    }

    const newOffset = {
      x: dragStartOffsetRef.current.x + dx,
      y: dragStartOffsetRef.current.y + dy,
    };
    offsetRef.current = newOffset;
    targetOffsetRef.current = newOffset;
    setOffset(newOffset);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
      containerRef.current.style.userSelect = '';
    }
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch handlers for pinch-to-zoom
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const t1 = touches[0];
    const t2 = touches[1];
    return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (panBlocked) return;
    if (e.touches.length === 2) {
      // Start pinch — cancel any single-finger pan
      userMovedRef.current = true;
      isDraggingRef.current = false;
      const dist = getTouchDistance(e.touches);
      if (dist) {
        pinchDistanceRef.current = dist;
        setIsPinching(true);
      }
    } else if (e.touches.length === 1) {
      // Single-finger pan — mirror onMouseDown logic
      if (ignorePanStart(e.target as HTMLElement)) return;
      if (!containerRef.current) return;
      isZoomAnimatingRef.current = false;
      targetOffsetRef.current = offsetRef.current;
      targetZoomRef.current = zoomRef.current;
      hasDraggedRef.current = false;
      isDraggingRef.current = true;
      setIsDragging(true);
      startXRef.current = e.touches[0].clientX;
      startYRef.current = e.touches[0].clientY;
      dragStartOffsetRef.current = { ...offsetRef.current };
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      // Pinch-to-zoom centered on midpoint
      const dist = getTouchDistance(e.touches);
      if (!dist || !pinchDistanceRef.current) return;

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;

      const scale = dist / pinchDistanceRef.current;
      const prevZoom = zoomRef.current;
      const newZoom = Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, prevZoom * scale));

      const newOffset = {
        x: midX - (midX - offsetRef.current.x) * (newZoom / prevZoom),
        y: midY - (midY - offsetRef.current.y) * (newZoom / prevZoom),
      };

      zoomRef.current = newZoom;
      offsetRef.current = newOffset;
      targetZoomRef.current = newZoom;
      targetOffsetRef.current = newOffset;
      setZoom(newZoom);
      setOffset(newOffset);
      pinchDistanceRef.current = dist;
      setIsPinching(true);
    } else if (e.touches.length === 1 && isDraggingRef.current && !isPinching) {
      // Single-finger pan
      const dx = e.touches[0].clientX - startXRef.current;
      const dy = e.touches[0].clientY - startYRef.current;
      if (Math.abs(dx) > MAP_DRAG_THRESHOLD || Math.abs(dy) > MAP_DRAG_THRESHOLD) {
        hasDraggedRef.current = true;
        userMovedRef.current = true;
      }
      const newOffset = {
        x: dragStartOffsetRef.current.x + dx,
        y: dragStartOffsetRef.current.y + dy,
      };
      offsetRef.current = newOffset;
      targetOffsetRef.current = newOffset;
      setOffset(newOffset);
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
      pinchDistanceRef.current = null;
    }
    if (e.touches.length === 0) {
      isDraggingRef.current = false;
      setIsDragging(false);
    }
  };

  /**
   * Was the gesture that produced this click a pan? Reads and clears the
   * flag, so a tap after a pan still counts (Birdseye's bay-click rule).
   */
  const consumeDrag = useCallback((): boolean => {
    if (!hasDraggedRef.current) return false;
    hasDraggedRef.current = false;
    return true;
  }, []);

  /** Has the user moved the map (pan, pinch, wheel) since the last reset? */
  const hasUserMoved = useCallback(() => userMovedRef.current, []);
  /** Forget earlier user movement (a fresh map, or the user asked for a fit). */
  const resetUserMoved = useCallback(() => { userMovedRef.current = false; }, []);

  return {
    containerRef,
    zoom,
    offset,
    bounds,
    isDragging,
    isPinching,
    zoomRef,
    fitToScreen,
    panTo,
    consumeDrag,
    hasUserMoved,
    resetUserMoved,
    handlers: { onMouseDown, onTouchStart, onTouchMove, onTouchEnd },
  };
}
